/* Copyright (c) 2020 Paul Nebel */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { interpret } from 'xstate';
import { isEqual } from 'lodash';

import createPlayHeadMachine from '../core/utils/play-head-machine';
import * as styles from '../styles/components.module.css';
import Player from './player';

let machineContext;

class PlayHead extends React.Component {
    static getDerivedStateFromProps(nextProps, prevState) {
        const {
            variant,
            playerMachineState,
            loopCallbackCompletionTime,
            metronomeState,
            chordIndex,
            chordIndexChangedAt,
            buttonEvent,
            buttonClickedAt,
            buttonToggleState,
        } = nextProps;

        if (
            loopCallbackCompletionTime > prevState.loopCallbackCompletionTime &&
            prevState.metronomeState === 'active' &&
            playerMachineState !== 'stopped'
        ) {
            prevState.playerLoopComplete();
        }

        const newState = {
            ...prevState,
            metronomeState,
            variant,
            chordIndex,
            chordIndexChangedAt,
        };

        if (
            loopCallbackCompletionTime !== undefined &&
            prevState.playerMachineState !== 'stopped'
        ) {
            newState.loopCallbackCompletionTime = loopCallbackCompletionTime;
        }
        if (playerMachineState !== undefined) {
            newState.playerMachineState = playerMachineState;
        }
        if (prevState.metronomeState !== 'active') {
            newState.loopCallbackCompletionTime = 0;
        }
        if (chordIndexChangedAt !== prevState.chordIndexChangedAt) {
            prevState.service.send({ type: 'SET_INDEX', chordIndex });
        }
        if (buttonClickedAt !== prevState.buttonClickedAt) {
            switch (buttonEvent) {
                case 'shuffle':
                    prevState.service.send({
                        type: 'SET_INDEX',
                        chordIndex: 0,
                    });
                    newState.buttonClickedAt = buttonClickedAt;
                    break;
                case 'repeat':
                    prevState.service.send({
                        type: 'SET_REPEAT',
                        toggleState: buttonToggleState,
                    });
                    newState.buttonClickedAt = buttonClickedAt;
                    break;
                default:
                // do nothing
            }
        }
        if (!isEqual(prevState, newState)) {
            return newState;
        } else {
            return null;
        }
    }

    constructor(props, context) {
        super(props, context);
        const { length } = props;
        this.updateState = this.updateState.bind(this);
        this.playerLoopComplete = this.playerLoopComplete.bind(this);
        this.service = interpret(createPlayHeadMachine(length)).onTransition(
            this.updateState
        );

        this.state = {
            ...this.props,
            playerLoopComplete: this.playerLoopComplete,
            loopCallbackCompletionTime: 0,
            playerMachineState: 'stopped',
            service: this.service,
        };

        this.props.notifyPlayHeadIndexUpdated(0, length);
    }

    updateState(playHeadMachine) {
        const context = playHeadMachine.context;
        if (!machineContext || context.repeat !== machineContext.repeat) {
            this.props.notifyPlayHeadToggelRepeat(context.repeat);
        }
        machineContext = context;
        if (context.index === context.previousIndex) {
            if (this.state.playerMachineState !== 'stopped') {
                if (context.repeat) {
                    this.service.send({ type: 'SET_INDEX', chordIndex: 0 });
                } else {
                    this.props.notifyPlayHeadComplete();
                }
            }
        } else {
            const context = playHeadMachine.context;
            this.props.notifyPlayHeadIndexUpdated(
                context.index,
                context.length
            );
        }
    }

    playerLoopComplete() {
        this.service.send('NEXT');
    }

    componentDidMount() {
        this.service.start();
    }

    componentWillUnmount() {
        this.service.stop();
    }

    updateStateMachine(message) {
        const { send } = this.service;
        send(message);
    }

    goToFirst() {
        this.updateStateMachine('FIRST');
    }

    goToPrev() {
        this.updateStateMachine('PREV');
    }

    goToNext() {
        this.updateStateMachine('NEXT');
    }

    goToLast() {
        this.updateStateMachine('LAST');
    }

    render() {
        const { variant } = this.state;
        const { loopDuration } = this.props;
        return (
            <div className="container">
                <div className="columns is-mobile is-gapless is-vcentred">
                    <div className="column is-2 has-text-centered is-hidden">
                        <button
                            className={`button ${variant} is-rounded ${styles.playerButton}`}
                            onClick={this.goToFirst}
                        >
                            <i className="fas fa-fast-backward is-size-5 mt-1"></i>
                        </button>
                    </div>
                    <div className="column is-2 has-text-centered is-hidden">
                        <button
                            className={`button ${variant} is-rounded  ${styles.playerButton}`}
                            onClick={this.goToPrev}
                        >
                            <i className="fas fa-step-backward is-size-5 mt-1"></i>
                        </button>
                    </div>
                    <div className="column has-text-centered">
                        <Player
                            variant={variant}
                            loopDuration={loopDuration}
                            bpm={60}
                        />
                    </div>
                    <div className="column is-2 has-text-centered is-hidden">
                        <button
                            className={`button ${variant} is-rounded ${styles.playerButton}`}
                            onClick={this.goToNext}
                        >
                            <i className="fas fa-step-forward is-size-5 mt-1"></i>
                        </button>
                    </div>
                    <div className="column is-2 has-text-centered is-hidden">
                        <button
                            className={`button ${variant} is-rounded  ${styles.playerButton}`}
                            onClick={this.goToLast}
                        >
                            <i className="fas fa-fast-forward is-size-5 mt-1"></i>
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = ({
    playerMachineState,
    loopCallbackCompletionTime,
    metronomeState,
    chordIndex,
    chordIndexChangedAt,
    buttonEvent,
    buttonClickedAt,
    buttonToggleState,
}) => {
    return {
        playerMachineState,
        loopCallbackCompletionTime,
        metronomeState,
        chordIndex,
        chordIndexChangedAt,
        buttonEvent,
        buttonClickedAt,
        buttonToggleState,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        notifyPlayHeadIndexUpdated: (index, length) =>
            dispatch({
                type: `PLAY_HEAD_INDEX_UPDATED`,
                index,
                length,
            }),
        notifyPlayHeadComplete: () =>
            dispatch({
                type: `PLAY_HEAD_COMPLETE`,
            }),
        notifyPlayHeadToggelRepeat: (repeat) =>
            dispatch({
                type: `PLAY_HEAD_TOGGLE_REPEAT`,
                repeat,
            }),
    };
};

PlayHead.propTypes = {
    variant: PropTypes.string,
    length: PropTypes.number,
    playerMachineState: PropTypes.string,
    loopCallbackCompletionTime: PropTypes.number,
    notifyPlayHeadIndexUpdated: PropTypes.func.isRequired,
    notifyPlayHeadComplete: PropTypes.func.isRequired,
    notifyPlayHeadToggelRepeat: PropTypes.func.isRequired,
    metronomeState: PropTypes.string,
    loopDuration: PropTypes.string,
    chordIndex: PropTypes.number,
    chordIndexChangedAt: PropTypes.number,
    buttonEvent: PropTypes.string,
    buttonClickedAt: PropTypes.number,
    buttonToggleState: PropTypes.bool,
};

PlayHead.defaultProps = {
    variant: 'is-primary',
    length: 12,
    loopDuration: '2m',
    buttonToggleState: true,
};

export default connect(mapStateToProps, mapDispatchToProps)(PlayHead);
